import { useContext, useMemo, useState } from 'react';
import { useLazyQuery, useMutation, useQuery, useApolloClient } from '@apollo/client';
import {
  Table,
  Loader,
  IColumn,
  SvgIcon,
  getDuration,
  LabelWithIcon,
  ILearningTable,
  IDashboardDetailsInfo,
  Button,
  Divider,
  Checkbox,
  ILearningTableData,
  ILearningItemCollection,
} from '@ascd/witsby-components';
import StarIcon from '@mui/icons-material/Star';
import { Box, Grid, Tooltip, IconButton } from '@mui/material';
import * as FileSaver from 'file-saver';
import { debounce, get, isEmpty, startCase } from 'lodash';
import { DateTime } from 'luxon';
import * as XLSX from 'xlsx';
import { LearnosityReportTable } from '@components/LearnosityReportTable';
import { ASCD_ID } from '@constants';
import { AppContext } from '@contexts/appContext';
import { DashboardContext, IDetailsPage, eDashboardActionType } from '@contexts/dashboardContext';
import DASHBOARD_DETAILS_INFO from '@graphql/schema/dashboardDetailsInfo.graphql';
import GET_EBOOK_URL from '@graphql/schema/getEbookUrl.graphql';
import LOG_EVENTS from '@graphql/schema/mutations/logEvents.graphql';
import TAGS from '@graphql/schema/tags.graphql';
import {
  ICourse,
  IEBook,
  IFilterCategory,
  IFilterCourseList,
  IReportData,
  ISelectedReport,
  ITableFilter,
} from '@types';
import {
  getIcon,
  showToast,
  dateFormat,
  handleGraphqlError,
  DASHBOARD_COUNTS_TYPES,
  T_DASHBOARD_FILTER_TYPES,
} from '@utils';

interface ISubCategory {
  value: string;
  parentId?: string | null;
}

const AdminDashboardTopLearnings = () => {
  const apolloClient = useApolloClient();

  const {
    state: { currentInstitution },
  } = useContext(AppContext);

  const {
    dispatch,
    state: { detailsPage, sideBarApiFilter },
  } = useContext(DashboardContext);

  const { detailsPageData } = detailsPage as IDetailsPage;

  const collectionType = (detailsPageData?.selectedLearning ||
    detailsPageData.collectionType) as T_DASHBOARD_FILTER_TYPES;

  const {
    assignedBy,
    internalId1,
    selectedInstitution,
    createdByOrganizationId,
    selectedGroup,
    dates: { startDate, endDate },
  } = sideBarApiFilter;

  const [selectedReportData, setSelectedReportData] = useState<ISelectedReport | null>(null);
  const [isAssessmentReportOpen, setAssessmentReportOpen] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState<ISubCategory[]>([]);
  const [confirmSelectedCategories, setConfirmSelectedCategories] = useState<string[]>([]);
  const [confirmSelectedSubCategories, setConfirmSelectedSubCategories] = useState<ISubCategory[]>(
    [],
  );

  const [filter, setFilter] = useState<ITableFilter>({
    page: 0,
    limit: 20,
    searchText: '',
    sortDirection: 'DESC',
    sortField:
      collectionType === 'COURSES' ||
      collectionType === 'LEARNING_LISTS' ||
      collectionType === 'STRATEGY'
        ? 'enrolled'
        : 'viewed',
    searchField: [
      'title',
      'data.learningItemCollection.title',
      'data.learningItem.title',
      'data.learningItemCollection.title',
      'data.title',
    ],
  });

  // Handle institution selection filter for ASCD admins
  const currentSelectedInstitution = useMemo(() => {
    if (!isEmpty(selectedInstitution)) return selectedInstitution;

    return {
      value: currentInstitution._id,
      label: currentInstitution.name,
    };
  }, [currentInstitution._id, currentInstitution.name, selectedInstitution]);

  const variables = {
    filter,
    endDate,
    startDate,
    assignedBy,
    titles: null,
    managerIds: null,
    organizationId:
      currentSelectedInstitution?.label === 'ALL' ? '' : currentSelectedInstitution.value,
    createdByOrganizationId: isEmpty(createdByOrganizationId)
      ? [currentSelectedInstitution.value, ASCD_ID]
      : createdByOrganizationId,
    schoolIds:
      selectedGroup.length > 0 ||
      currentSelectedInstitution?.label === 'ALL' ||
      currentSelectedInstitution?.label === 'ASCD'
        ? null
        : internalId1.map((internalId) => internalId?.internalId),
    groupIds: (selectedGroup || []).map((val) => val._id),
    type: collectionType,
    strategyFilter: {
      category: confirmSelectedCategories,
      subCategory: confirmSelectedSubCategories.map((val) => val.value),
    },
  };

  const { loading, data: dashboardDetailsInfoData } = useQuery(DASHBOARD_DETAILS_INFO, {
    variables,
    fetchPolicy: 'network-only',
  });

  const dashboardActivityInfoList = get(dashboardDetailsInfoData, 'dashboardDetailsInfo[0]', {
    totalCount: 0,
    viewCount: [],
    collections: [],
    completedCount: [],
    totalClockHours: 0,
    dashBoardType: collectionType,
  });

  const { loading: tagLoading, data: tagData } = useQuery(TAGS, {
    variables: {
      status: 'ACTIVE',
      scope: 'STRATEGY',
    },
  });

  const tags: IFilterCategory[] = get(tagData, 'tags', []);

  const categories = useMemo(() => {
    const categoriesFilter = tags.filter((tag) => tag.type === 'SUB-CATEGORY');
    return categoriesFilter;
  }, [tags]);

  const subCategories = useMemo(() => {
    const subCategoriesFilter = tags.filter((tag) => tag.type === 'TAG');
    return subCategoriesFilter;
  }, [tags]);

  const [logEvents] = useMutation(LOG_EVENTS);

  // when click on ebooks it's fetch url and open pdf
  const onOpenEBook = async (item?: IEBook | ICourse | IFilterCourseList) => {
    if (!Object.prototype.hasOwnProperty.call(item, 'LockLizardBookId')) return;
    if ('LockLizardBookId' in (item as ICourse | IEBook | IFilterCourseList)) {
      const response = await apolloClient.query({
        query: GET_EBOOK_URL,
        variables: { docId: (item as IEBook)?.LockLizardBookId },
      });
      if (response?.data?.getEbookUrl) {
        logEvents({
          variables: {
            data: item,
            logType: 'EBOOK',
            actionType: 'VIEWED',
          },
        });
        window.open(response.data.getEbookUrl, '_blank');
      }
    }
  };

  const filterView = () => (
    <Box
      sx={{
        width: 300,
        borderRadius: '0.625rem',
      }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
        <Box
          sx={{
            color: (theme) => theme.palette.grey[100],
            fontSize: (theme) => theme.font.size.REGULAR,
          }}>
          Filter
        </Box>
        <Box
          sx={(theme) => ({
            fontSize: theme.font.size.X_SMALL,
            color: theme.palette.primary.main,
            cursor: 'pointer',
          })}
          onClick={() => {
            setSelectedCategories([]);
            setSelectedSubCategories([]);
          }}>
          Clear all
        </Box>
      </Box>
      <Divider sx={{ my: '0.625rem' }} />
      <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box
          sx={{
            fontSize: (theme) => theme.font.size.SMALL,
          }}>
          DISPLAY
        </Box>

        <Checkbox
          label="Select all"
          checked={
            selectedCategories.length === categories.length &&
            selectedSubCategories.length === subCategories.length
          }
          onHandleChange={(e) => {
            if (e.target.checked) {
              setSelectedCategories(categories.map((val) => val.value));
              setSelectedSubCategories(
                subCategories.map((val) => ({ parentId: val.parentId, value: val.value })),
              );
            } else {
              setSelectedCategories([]);
              setSelectedSubCategories([]);
            }
          }}
        />
      </Box>

      <Box sx={{ px: 2, maxHeight: '50vh', overflow: 'scroll' }}>
        {categories.map((item) => (
          <Box key={item._id}>
            <Checkbox
              checkboxSx={{
                py: '5px',
              }}
              label={item.title}
              checked={selectedCategories.includes(item.value)}
              onHandleChange={() => {
                if (selectedCategories.includes(item.value)) {
                  setSelectedCategories((prev) => prev.filter((val) => val !== item.value));
                  setSelectedSubCategories((prev) =>
                    prev.filter((val) => val.parentId !== item._id),
                  );
                } else {
                  const subCat = subCategories.filter((sItem) => sItem.parentId === item._id);
                  setSelectedCategories((prev) => [...prev, item.value]);
                  setSelectedSubCategories((prev) => [
                    ...prev,
                    ...subCat.map((val) => ({ value: val.value, parentId: val.parentId })),
                  ]);
                }
              }}
            />
            {subCategories
              .filter((sItem) => sItem.parentId === item._id)
              .map((mItem) => (
                <Box key={mItem._id} sx={{ ml: 2 }}>
                  <Checkbox
                    checkboxSx={{
                      py: '5px',
                    }}
                    label={mItem.title}
                    checked={selectedSubCategories.some((val) => val.value === mItem.value)}
                    onHandleChange={() => {
                      if (selectedSubCategories.some((val) => val.value === mItem.value)) {
                        const currentSub = selectedSubCategories.filter(
                          (val) => val.parentId === mItem.parentId,
                        );
                        setSelectedSubCategories((prev) =>
                          prev.filter((val) => val.value !== mItem.value),
                        );
                        if (currentSub.length === 1) {
                          setSelectedCategories((prev) => prev.filter((val) => val !== item.value));
                        }
                      } else {
                        const selectedCat = categories.find(
                          (val) => val._id === mItem.parentId,
                        )?.value;
                        if (selectedCat && !selectedCategories.includes(selectedCat)) {
                          setSelectedCategories((prev) => [...prev, selectedCat]);
                        }
                        setSelectedSubCategories((prev) => [
                          ...prev,
                          { value: mItem.value, parentId: mItem.parentId },
                        ]);
                      }
                    }}
                  />
                </Box>
              ))}
          </Box>
        ))}
      </Box>
      {/* Error Message  */}
      {/* {checkedList.length === 0 && (
        <Box
          sx={{
            px: 2,
            color: (theme) => theme.palette.error.light,
            fontSize: (theme) => theme.font.size.XX_SMALL,
          }}>
          Please select one or more status and click Apply.
        </Box>
      )} */}

      <Divider sx={{ my: '0.625rem' }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, gap: '0.5rem' }}>
        <Button
          size="small"
          onClick={() => {
            setOpenFilter(false);
            setSelectedCategories(confirmSelectedCategories);
            setSelectedSubCategories(confirmSelectedSubCategories);
          }}
          sx={{
            borderRadius: '0.5rem',
            px: '0.625rem',
            '&': {
              color: (theme) => theme.palette.grey[300],
              borderColor: (theme) => theme.palette.grey[300],
            },
            '&:hover': {
              color: (theme) => theme.palette.white[100],
              borderColor: (theme) => theme.palette.grey[300],
              backgroundColor: (theme) => theme.palette.grey[300],
            },
          }}>
          Cancel
        </Button>
        <Button
          // disabled={checkedList.length === 0 || checkedTypeList.length === 0}
          size="small"
          sx={{
            borderRadius: '0.5rem',
            px: '0.625rem',
            '&, &.Mui-disabled': {
              color: (theme) => theme.palette.primary.light,
            },
          }}
          onClick={() => {
            setOpenFilter(false);
            setFilter({ ...filter, page: 0 });
            setConfirmSelectedCategories(selectedCategories);
            setConfirmSelectedSubCategories(selectedSubCategories);
          }}>
          Apply
        </Button>
      </Box>
    </Box>
  );

  let learningColumnWidth = '35%';

  if (
    collectionType === 'COURSES' ||
    collectionType === 'LEARNING_LISTS' ||
    collectionType === 'STRATEGY' ||
    collectionType === 'LEARNING_ITEMS' ||
    collectionType === 'RESOURCES'
  ) {
    learningColumnWidth = '35%';
  }

  if (collectionType === 'EL_MAGAZINES' || collectionType === 'EL_MAGAZINE') {
    learningColumnWidth = '70%';
  }

  let learningColumn: IColumn[] = [];

  if (collectionType !== 'STRATEGY') {
    learningColumn = [
      {
        key: 'title',
        name: 'Title',
        width: learningColumnWidth,
        isSorting: true,
        ...(filter.sortField === 'title' && {
          sortType: filter.sortDirection,
        }),
        render: (_, value) => {
          const { data } = value as IDashboardDetailsInfo;
          const getCollectionTypeDetails = () => {
            if (
              collectionType !== 'WEBINARS' &&
              collectionType !== 'WEBINAR' &&
              collectionType !== 'EBOOKS' &&
              collectionType !== 'EL_MAGAZINE' &&
              collectionType !== 'EL_MAGAZINES'
            ) {
              dispatch({
                type: 'DETAILS_PAGE' as eDashboardActionType.DETAILS_PAGE,
                data: {
                  type: 'COLLECTION_TYPE_DETAIL',
                  detailsPageData: { value, collectionType, prevScreen: 'TOP_LEARNINGS' },
                },
              });
            }
          };

          return (
            <Box
              tabIndex={0}
              sx={{
                cursor: 'pointer',
                '&:hover,&:focus': {
                  textDecoration: 'underline',
                  outline: 'none',
                  color: (theme) => theme.palette.primary.light,
                },
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  getCollectionTypeDetails();
                }
              }}
              onClick={getCollectionTypeDetails}>
              {(collectionType === 'COURSES' ||
                collectionType === 'LEARNING_LISTS' ||
                collectionType === 'LEARNING_ITEMS' ||
                collectionType === 'RESOURCES') && (
                <Box>{(value as IDashboardDetailsInfo)?.title || 'Untitled'}</Box>
              )}

              {(collectionType === 'WEBINAR' || collectionType === 'WEBINARS') && (
                <Box
                  onClick={() => {
                    if ((value as IDashboardDetailsInfo)?._id) {
                      window.open(
                        `https://www.ascd.org/el/${(value as IDashboardDetailsInfo)?._id}`,
                        '_blank',
                        'noopener,noreferrer',
                      );
                    }
                  }}>
                  {(value as IDashboardDetailsInfo)?.title}
                </Box>
              )}
              {collectionType === 'EBOOKS' && (
                <Box onClick={() => onOpenEBook(data as IEBook)}>{(data as IEBook)?.title}</Box>
              )}
              {(collectionType === 'EL_MAGAZINE' || collectionType === 'EL_MAGAZINES') && (
                <Box
                  onClick={() => {
                    if ((value as IDashboardDetailsInfo)?._id) {
                      window.open(
                        `https://www.ascd.org/el/${(value as IDashboardDetailsInfo)?._id}`,
                        '_blank',
                        'noopener,noreferrer',
                      );
                    }
                  }}>
                  {(value as IDashboardDetailsInfo)?.title}
                </Box>
              )}
            </Box>
          );
        },
      },
      {
        key: 'viewed',
        name: 'Views',
        align: 'center',
        headerAlign: 'center',
        isSorting: true,
        ...(filter.sortField === 'viewed' && {
          sortType: filter.sortDirection,
        }),
        render: (_, value) => <Box>{(value as IDashboardDetailsInfo)?.viewed || 0}</Box>,
      },
      ...(collectionType === 'COURSES' ||
      collectionType === 'LEARNING_LISTS' ||
      collectionType === 'LEARNING_ITEMS' ||
      collectionType === 'RESOURCES'
        ? ([
            ...(collectionType !== 'LEARNING_ITEMS' && collectionType !== 'RESOURCES'
              ? ([
                  {
                    key: 'enrolled',
                    ...(collectionType === 'COURSES' && {
                      name: 'Started',
                    }),
                    ...(collectionType === 'LEARNING_LISTS' && {
                      name: 'Added',
                    }),
                    align: 'center',
                    headerAlign: 'center',
                    isSorting: true,
                    ...(filter.sortField === 'enrolled' && {
                      sortType: filter.sortDirection,
                    }),
                    render: (_, value) => (
                      <Box>
                        <Box>{(value as IDashboardDetailsInfo)?.enrolled || 0}</Box>
                      </Box>
                    ),
                  },
                  {
                    key: 'rating',
                    name: 'Rating',
                    align: 'center',
                    headerAlign: 'center',
                    isSorting: true,
                    ...(filter.sortField === 'rating' && {
                      sortType: filter.sortDirection,
                    }),
                    render: (_, value) => (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <StarIcon sx={{ color: '#faaf00' }} />{' '}
                        <Box>{(value as IDashboardDetailsInfo)?.rating?.toFixed(2) || '0.00'}</Box>
                      </Box>
                    ),
                  },
                ] as IColumn[])
              : []),
            {
              key: 'completed',
              name: 'Completed',
              align: 'center',
              headerAlign: 'center',
              isSorting: true,
              ...(filter.sortField === 'completed' && {
                sortType: filter.sortDirection,
              }),
              render: (_, value) => <Box>{(value as IDashboardDetailsInfo)?.completed || 0}</Box>,
            },
            {
              key: 'clockHours',
              name: 'Clock Hours',
              align: 'center',
              headerAlign: 'center',
              isSorting: true,
              ...(filter.sortField === 'clockHours' && {
                sortType: filter.sortDirection,
              }),
              render: (_, value) => (
                <Box>{getDuration((value as IDashboardDetailsInfo)?.clockHours || 0, false)}</Box>
              ),
            },
          ] as IColumn[])
        : []),
      ...(collectionType === 'EBOOKS' ||
      collectionType === 'WEBINAR' ||
      collectionType === 'WEBINARS' ||
      collectionType === 'EL_MAGAZINE' ||
      collectionType === 'EL_MAGAZINES'
        ? ([
            {
              key: 'uniqueAccessCount',
              name: 'Unique Access',
              align: 'center',
              headerAlign: 'center',
              isSorting: true,
              ...(filter.sortField === 'uniqueAccessCount' && {
                sortType: filter.sortDirection,
              }),
              render: (_, value) => (
                <Box>{(value as IDashboardDetailsInfo)?.uniqueAccessCount || 0}</Box>
              ),
            },
          ] as IColumn[])
        : []),
      {
        key: 'lastAccess',
        name: 'Last Updated',
        render: (_, value) => (
          <Box>
            <Box>
              {(value as IDashboardDetailsInfo)?.lastAccess
                ? DateTime.fromJSDate(
                    new Date((value as IDashboardDetailsInfo)?.lastAccess),
                  ).toFormat(`''LL'/'dd'/'yyyy'`)
                : '-'}
            </Box>
          </Box>
        ),
        isSorting: true,
        ...(filter.sortField === 'lastAccess' && {
          sortType: filter.sortDirection,
        }),
      },
      ...(collectionType === 'COURSES'
        ? ([
            {
              name: '',
              align: 'center',
              key: 'assessment-certificate',
              headerAlign: 'center',
              render: (_, value) => (
                <Box>
                  {collectionType === 'COURSES' && (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '1.25rem',
                      }}>
                      {get(value as ILearningTable, 'assessments', [])?.length > 0 && (
                        <Tooltip describeChild title="Assessment" arrow>
                          <IconButton
                            onClick={() => {
                              setSelectedReportData({
                                reportData: {
                                  type: 'COURSE',
                                  id: (value as IDashboardDetailsInfo)?._id,
                                  title: (value as IDashboardDetailsInfo)?.title,
                                },
                              });
                              setAssessmentReportOpen(true);
                            }}>
                            <SvgIcon icon="ASSESSMENT" sx={{ cursor: 'pointer', fill: 'none' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </Box>
              ),
            },
          ] as IColumn[])
        : []),
    ];
  }

  if (collectionType === 'STRATEGY') {
    learningColumn = [
      {
        key: 'title',
        name: 'Title',
        isSorting: true,
        ...(filter.sortField === 'title' && {
          sortType: filter.sortDirection,
        }),
        render: (_, value) => {
          const getCollectionTypeDetails = () => {
            dispatch({
              type: 'DETAILS_PAGE' as eDashboardActionType.DETAILS_PAGE,
              data: {
                type: 'COLLECTION_TYPE_DETAIL',
                detailsPageData: { value, collectionType, prevScreen: 'TOP_LEARNINGS' },
              },
            });
          };
          return (
            <Box
              tabIndex={0}
              sx={{
                cursor: 'pointer',
                '&:hover,&:focus': {
                  textDecoration: 'underline',
                  outline: 'none',
                  color: (theme) => theme.palette.primary.light,
                },
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  getCollectionTypeDetails();
                }
              }}
              onClick={getCollectionTypeDetails}>
              {(value as IDashboardDetailsInfo).title || 'Untitled'}
            </Box>
          );
        },
      },
      {
        key: 'category',
        name: 'Category',
        isSorting: true,
        ...(filter.sortField === 'category' && {
          sortType: filter.sortDirection,
        }),
        render: (_, value) => <Box>{startCase((value as IDashboardDetailsInfo).category)}</Box>,
      },
      {
        key: 'subCategory',
        name: 'Subcategory',
        isSorting: true,
        ...(filter.sortField === 'subCategory' && {
          sortType: filter.sortDirection,
        }),
        render: (_, value) => <Box>{startCase((value as IDashboardDetailsInfo).subCategory)}</Box>,
      },
      {
        key: 'viewed',
        name: 'Views',
        align: 'center',
        headerAlign: 'center',
        isSorting: true,
        ...(filter.sortField === 'viewed' && {
          sortType: filter.sortDirection,
        }),
      },
      {
        key: 'enrolled',
        name: 'Adopted',
        align: 'center',
        headerAlign: 'center',
        isSorting: true,
        ...(filter.sortField === 'enrolled' && {
          sortType: filter.sortDirection,
        }),
      },
      {
        key: 'rating',
        name: 'Rating',
        align: 'center',
        headerAlign: 'center',
        isSorting: true,
        ...(filter.sortField === 'rating' && {
          sortType: filter.sortDirection,
        }),
        render: (_, value) => (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <StarIcon sx={{ color: '#faaf00' }} />{' '}
            <Box>{(value as IDashboardDetailsInfo)?.rating?.toFixed(2) || '0.00'}</Box>
          </Box>
        ),
      },
      {
        key: 'completed',
        name: 'Completed',
        align: 'center',
        headerAlign: 'center',
        isSorting: true,
        ...(filter.sortField === 'completed' && {
          sortType: filter.sortDirection,
        }),
      },
      {
        key: 'clockHours',
        name: 'Clock Hours',
        align: 'center',
        headerAlign: 'center',
        isSorting: true,
        ...(filter.sortField === 'clockHours' && {
          sortType: filter.sortDirection,
        }),
        render: (_, value) => (
          <Box>{getDuration((value as IDashboardDetailsInfo)?.clockHours || 0, false)}</Box>
        ),
      },
      {
        key: 'lastAccess',
        name: 'Last Updated',
        render: (_, value) => (
          <Box>
            <Box>
              {(value as IDashboardDetailsInfo)?.lastAccess
                ? DateTime.fromJSDate(
                    new Date((value as IDashboardDetailsInfo)?.lastAccess),
                  ).toFormat(`''LL'/'dd'/'yyyy'`)
                : '-'}
            </Box>
          </Box>
        ),
        isSorting: true,
        ...(filter.sortField === 'lastAccess' && {
          sortType: filter.sortDirection,
        }),
      },
    ];
  }

  const [dashboardActivityInfoCSV, { loading: isCSVLoading }] = useLazyQuery(
    DASHBOARD_DETAILS_INFO,
    { fetchPolicy: 'network-only' },
  );

  const exportToCSV = async () => {
    if (!isCSVLoading) {
      dashboardActivityInfoCSV({
        variables: {
          ...variables,
          filter: {
            ...filter,
            page: 0,
            limit: dashboardActivityInfoList.totalCount,
          },
        },
      })
        .then((res) => {
          const dashboardActivityCSVInfo = get(res.data, 'dashboardDetailsInfo[0]', {
            totalCount: 0,
            viewCount: [],
            collections: [],
            completedCount: [],
            totalClockHours: 0,
            dashBoardType: collectionType,
          });
          let datalist = [];
          const csvData = get(dashboardActivityCSVInfo, 'collections', []);
          if (
            collectionType === 'COURSES' ||
            collectionType === 'LEARNING_LISTS' ||
            collectionType === 'LEARNING_ITEMS' ||
            collectionType === 'RESOURCES'
          ) {
            datalist = csvData.map((item: IDashboardDetailsInfo) => ({
              Title: item?.title || 'Untitled',
              Views: item?.viewed,
              ...(collectionType === 'COURSES' && {
                Started: item?.enrolled,
              }),
              ...(collectionType === 'LEARNING_LISTS' && {
                Added: item?.enrolled,
              }),
              Completed: get(item, 'completed', 0) || 0,
              ...(collectionType !== 'LEARNING_ITEMS' && {
                Rating: item?.rating?.toFixed(2) || '0.00',
              }),
              'Clock Hours': getDuration(item?.clockHours || 0, false),
              Type: DASHBOARD_COUNTS_TYPES[`${collectionType}`],
              'Last Updated': item?.lastAccess
                ? dateFormat({ date: item?.lastAccess, format: 'dd-MMM-yyyy' })
                : '-',
            }));
          }

          if (collectionType === 'STRATEGY') {
            datalist = csvData.map((item: IDashboardDetailsInfo) => ({
              Title: item?.title || 'Untitled',
              Category: item?.category,
              Subcategory: item?.subCategory,
              Views: item?.viewed,
              Adopted: item?.enrolled,
              Rating: item?.rating?.toFixed(2) || '0.00',
              Completed: item.completed || 0,
              'Clock Hours': getDuration(item?.clockHours || 0, false),
              Type: DASHBOARD_COUNTS_TYPES[`${collectionType}`],
              'Last Updated': item?.lastAccess
                ? dateFormat({ date: item?.lastAccess, format: 'dd-MMM-yyyy' })
                : '-',
            }));
          }

          if (
            collectionType === 'WEBINAR' ||
            collectionType === 'WEBINARS' ||
            collectionType === 'EBOOKS' ||
            collectionType === 'EL_MAGAZINE' ||
            collectionType === 'EL_MAGAZINES'
          ) {
            datalist = csvData.map((item: IDashboardDetailsInfo) => ({
              Title: item.title,
              Views: item.viewed,
              'Unique Access': item.uniqueAccessCount || 0,
              Type: DASHBOARD_COUNTS_TYPES[`${collectionType}`],
              'Last Updated': item?.lastAccess
                ? dateFormat({ date: item?.lastAccess, format: 'dd-MMM-yyyy' })
                : '-',
            }));
          }
          const fileType =
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
          const fileExtension = '.xlsx';
          const ws = XLSX.utils.json_to_sheet(datalist);
          const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
          const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
          const data = new Blob([excelBuffer], { type: fileType });
          FileSaver.saveAs(
            data,
            `Top ${startCase(collectionType.toLowerCase())} (${dateFormat({
              date: startDate,
              format: 'dd-MMM-yyyy',
            })} - ${dateFormat({ date: endDate, format: 'dd-MMM-yyyy' })}) ${fileExtension}`,
          );
          showToast('Downloaded Successfully');
        })
        .catch(handleGraphqlError);
    }
  };

  if (isAssessmentReportOpen) {
    return (
      <LearnosityReportTable
        reportData={selectedReportData?.reportData as IReportData}
        setAssessmentReportOpen={setAssessmentReportOpen}
      />
    );
  }

  // const getTitle = (type: T_DASHBOARD_FILTER_TYPES): string => {
  //   if (type === 'COURSES' || type === 'LEARNING_LISTS' || type === 'LEARNING_ITEMS') {
  //     return `${startCase(collectionType.toLowerCase()) || 'Learnings'}: (${
  //       dashboardActivityInfoList?.totalCount
  //     } Completed | ${getDuration(
  //       dashboardActivityInfoList?.totalClockHours || 0,
  //       false,
  //     )} clocked)`;
  //   }
  //   if (type === 'EBOOKS') {
  //     const TotalViews = dashboardActivityInfoList?.collections[0]?.groupForViews.reduce(
  //       (sum: number, item: IGroupForViews) => sum + item.totalViews,
  //       0,
  //     );
  //     return `Ebooks: (${TotalViews} Views)`;
  //   }
  //   if (type === 'WEBINAR' || type === 'WEBINARS') {
  //     const TotalViews = dashboardActivityInfoList?.collections[0]?.groupForViews.reduce(
  //       (sum: number, item: IGroupForViews) => sum + item.totalViews,
  //       0,
  //     );
  //     return `Webinars: (${TotalViews} Views)`;
  //   }
  //   if (type === 'EL_MAGAZINE' || type === 'EL_MAGAZINES') {
  //     const TotalViews = dashboardActivityInfoList?.collections[0]?.groupForViews.reduce(
  //       (sum: number, item: IGroupForViews) => sum + item.totalViews,
  //       0,
  //     );
  //     return `EL Magazine: (${TotalViews} Views)`;
  //   }
  //   return '';
  // };

  return (
    <Box sx={{ mt: 1, mb: 5 }}>
      <Box>
        <Grid container spacing={3}>
          <Grid item xl={12} sm={12} xs={12}>
            <Table
              isSearchEnable
              isFilterEnable={!tagLoading && categories.length > 0 && collectionType === 'STRATEGY'}
              loading={loading}
              page={filter.page}
              apiPaginationEnable
              openFilter={openFilter}
              pageSize={filter.limit}
              columns={learningColumn}
              searchValue={filter.searchText}
              searchPlaceHolder="Search Title"
              activeSortField={filter.sortField}
              filterPopoverChildren={filterView()}
              onOpenFilter={() => setOpenFilter(true)}
              count={dashboardActivityInfoList.totalCount}
              dataEmptyChildren={<Box>No learnings found</Box>}
              data={get(dashboardActivityInfoList, 'collections')}
              onCloseFilter={() => {
                setOpenFilter(false);
                setSelectedCategories(confirmSelectedCategories);
                setSelectedSubCategories(confirmSelectedSubCategories);
              }}
              handlePageChange={(_, newPage) => {
                setFilter({ ...filter, page: newPage });
              }}
              onHandleSearch={debounce((e) => {
                setFilter({ ...filter, page: 0, searchText: e.target.value });
              }, 500)}
              handleRowsPerPageChange={(e) => {
                setFilter({ ...filter, page: 0, limit: parseInt(e.target.value, 10) });
              }}
              onSortClick={(key, sortType) => {
                setFilter({
                  ...filter,
                  page: 0,
                  sortField: key as string,
                  sortDirection: sortType,
                });
              }}
              headerChildren={
                <Box>
                  <LabelWithIcon
                    // title={`Top ${
                    //   loading ? startCase(collectionType.toLowerCase()) : getTitle(collectionType)
                    // }`}
                    title={`Top ${
                      collectionType === 'STRATEGY'
                        ? 'Strategies'
                        : startCase(collectionType.toLowerCase())
                    }`}
                    iconName={getIcon(collectionType)}
                    iconDirection="left"
                    iconColorHex="#FFFFFF"
                    sx={{ color: '#FFFFFF' }}
                  />
                </Box>
              }
              headerExtra={
                dashboardActivityInfoList.totalCount > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    {isCSVLoading ? (
                      <Loader
                        sx={{ color: '#FFFFFF', ml: (theme) => theme.spaces.LARGE }}
                        size={20}
                      />
                    ) : (
                      <Tooltip describeChild title="Download the report below" arrow>
                        <IconButton onClick={() => exportToCSV()} aria-label="Handle course">
                          <SvgIcon
                            icon="FILE_DOWNLOAD"
                            viewBox="0 0 22 29"
                            sx={{ fill: '#FFFF' }}
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )
              }
              getRowSx={(row) => {
                if (row?.courseStatus === 'UNPUBLISHED' || row?.courseStatus === 'ARCHIVED') {
                  return {
                    background: (theme) => theme.palette.grey.A100,
                  };
                }
                return undefined;
              }}
              aboveTableHeaderChildren={
                collectionType === 'COURSES' && (
                  <Box
                    sx={{
                      p: 1.2,
                      pt: 0,
                      pr: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                    }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1.563rem',
                      }}>
                      <LabelWithIcon
                        title="Assessment"
                        iconName="ASSESSMENT"
                        iconDirection="left"
                        iconColorHex="none"
                        isClickable={false}
                        sx={{ fontWeight: (theme) => theme.font.weight.LIGHT }}
                      />
                    </Box>
                  </Box>
                )
              }
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AdminDashboardTopLearnings;
